import React, { PropsWithChildren, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import ErrorText from "../../components/errorText";
import * as yup from "yup";
import update from "immutability-helper";
import { Logger } from '../../utils/logger';
import GlobalDrawer from "../../components/GlobalDrawer";
import { toast } from "react-hot-toast";

interface WebhookData {
  webhookUrl: string;
  requestMethod: string;
  webhookServiceType?: string;
  reqBody: string;
  reqHeader: Record<string, string>;
  mapper: Record<string, string>;
}

interface TeamMember {
  value: string;
  label: string;
  type: string;
}

interface Props {
  addUpdateModalValue: any;
  onSaveDrawerClose: (obj: any) => void;
}

const ActionWebhook: React.FC<PropsWithChildren<Props>> = ({
  addUpdateModalValue,
  onSaveDrawerClose,
}) => {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [assignLeadDrawer, setAssignLeadDrawer] = useState(false);

  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });

  const teamMembers: TeamMember[] =
    StoreData?.userData?.userDetails?.userPreference?.customForm?.map(
      (item: any) => {
        return {
          value: item?.value,
          label: item?.name,
          type: item?.type,
        };
      }
    ) || [];

  const [webhookData, setWebhookData] = useState<WebhookData>({
    webhookUrl: "",
    requestMethod: "POST",
    webhookServiceType: "",
    reqBody: "",
    reqHeader: {
      "Content-Type": "application/json"
    },
    mapper: {}
  });

  // Fixed headers - not editable by users
  const fixedHeaders = {
    "Content-Type": "application/json"
  };

  const [errors, setErrors] = useState({
    webhookUrl: "",
    reqBody: "",
  });

  useEffect(() => {
    if (Object.values(addUpdateModalValue).length > 0) {
      const data = addUpdateModalValue;

      setWebhookData({
        webhookUrl: data.webhookUrl || "",
        requestMethod: data.requestMethod || "POST",
        webhookServiceType: data.webhookServiceType || "",
        reqBody: data.reqBody || "",
        reqHeader: data.reqHeader || fixedHeaders,
        mapper: data.mapper || {}
      });
    }
  }, [addUpdateModalValue]);

  let schema = yup.object().shape({
    webhookUrl: yup.string()
      .url("Invalid URL format")
      .matches(/^https:\/\//, "URL must use HTTPS")
      .required("Webhook URL is required"),
    reqBody: yup.string().max(2000, "Max 2000 characters are allowed"),
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.currentTarget;
    setWebhookData({
      ...webhookData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  // Headers are now fixed, no management functions needed

  const insertLeadField = (item: TeamMember) => {
    const fieldText = `{{${item.value}}}`;
    const textarea = bodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = webhookData.reqBody.substring(0, start);
    const textAfter = webhookData.reqBody.substring(end);
    const newValue = `${textBefore}${fieldText}${textAfter}`;

    if (newValue.length <= 2000) {
      setWebhookData({ ...webhookData, reqBody: newValue });
      setAssignLeadDrawer(false);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + fieldText.length,
          start + fieldText.length
        );
      }, 0);
    } else {
      toast.error("Adding this field would exceed the 2000 character limit");
    }
  };

  // Auto-generate mapper from {{field}} patterns in request body
  const generateMapper = (text: string): Record<string, string> => {
    const matches = text.match(/\{\{(.*?)\}\}/g);
    if (!matches) return {};

    const mapper: Record<string, string> = {};
    matches.forEach(match => {
      const fieldName = match.replace(/\{\{|\}\}/g, "").trim();
      // CRM field name (left) maps to itself as webhook variable (right)
      mapper[fieldName] = fieldName;
    });
    return mapper;
  };

  const handleWebhookSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const isFormValid = await schema.isValid(webhookData, {
      abortEarly: false,
    });

    if (isFormValid) {
      // Use fixed headers
      const reqHeader = fixedHeaders;

      // Auto-generate mapper from request body {{field}} patterns
      const mapper = generateMapper(webhookData.reqBody);

      // Build the final object - only include fields that have values
      const tempObj: any = {
        webhookUrl: webhookData.webhookUrl,
        requestMethod: webhookData.requestMethod,
        reqHeader,
        mapper
      };

      // Only add webhookServiceType if it has a value
      if (webhookData.webhookServiceType) {
        tempObj.webhookServiceType = webhookData.webhookServiceType;
      }

      // Only add reqBody if it has content
      if (webhookData.reqBody) {
        tempObj.reqBody = webhookData.reqBody;
      }

      Logger.log('Webhook data being saved:', tempObj);
      onSaveDrawerClose(tempObj);
    } else {
      schema.validate(webhookData, { abortEarly: false }).catch((err) => {
        const errors = err.inner.reduce(
          (acc: any, error: { path: string; message: string }) => {
            return {
              ...acc,
              [error.path]: error.message,
            };
          },
          {}
        );
        setErrors((prevErrors) =>
          update(prevErrors, {
            $set: errors,
          })
        );
      });
    }
  };

  return (
    <>
      <div className="popup-section-1 popup-section-2 popup-section-3">
        <div>
          <div className="form-group">
            <label className="form-label">Webhook URL (HTTPS only)</label>
            <input
              type="text"
              value={webhookData?.webhookUrl}
              onChange={handleChange}
              name="webhookUrl"
              className="form-control"
              placeholder="https://api.example.com/webhook"
            />
            {errors.webhookUrl ? <ErrorText message={errors?.webhookUrl} /> : ""}
          </div>

          <div className="form-group">
            <label className="form-label">Service Type (Optional)</label>
            <select
              value={webhookData?.webhookServiceType}
              onChange={handleChange}
              name="webhookServiceType"
              className="form-select"
            >
              <option value="">-- Select Service Type (Optional) --</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="custom">Custom</option>
            </select>
            <small className="form-text text-muted">
              Optional: Specify the webhook service type if needed
            </small>
          </div>


          <div className="form-group">
            <label className="form-label">Request Body (JSON) - Optional</label>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <textarea
                  ref={bodyRef}
                  className="form-control"
                  value={webhookData?.reqBody}
                  onChange={handleChange}
                  name="reqBody"
                  style={{ height: "200px" }}
                  placeholder='{"chatId": "{{phone}}", "message": "Hello {{firstName}}, your order is ready!"}'
                ></textarea>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  <span>Use {`{{fieldName}}`} to insert lead data. Field mappings will be auto-generated.</span>
                  <span>{webhookData.reqBody.length}/2000</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAssignLeadDrawer(true)}
                className="btn btn-outline-primary btn-sm"
                style={{ whiteSpace: 'nowrap' }}
              >
                Insert Field
              </button>
            </div>
            <small className="form-text text-muted">
              Optional: Add a JSON body with {`{{fieldName}}`} placeholders. The system will automatically create field mappings.
            </small>
            {errors.reqBody ? <ErrorText message={errors?.reqBody} /> : ""}
          </div>
        </div>

        <div className="auto_form_btn">
          <button
            type="button"
            onClick={handleWebhookSubmit}
            className="btn btn-primary-save"
          >
            SAVE
          </button>
        </div>
      </div>

      <GlobalDrawer
        openWithHeader={assignLeadDrawer}
        setOpenWithHeader={() => setAssignLeadDrawer(false)}
        drawerTitle={"Insert Lead Field"}
        size="xs"
      >
        <div>
          {teamMembers?.map((item: TeamMember, ix: number) => {
            return (
              <button
                key={ix}
                onClick={(e) => {
                  e.preventDefault();
                  insertLeadField(item);
                }}
                className="d-block bg-white w-100 p-3 rounded mb-2"
                style={{ margin: "8px 0px", textAlign: "left" }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </GlobalDrawer>
    </>
  );
};

export default ActionWebhook;